import {
    Flex,
    Box,
    Button,
    Grid,
    Input,
    Select,
    createListCollection,
    Text,
    VStack,
    HStack,
    Dialog,
    Image,
} from "@chakra-ui/react";

import {
    Edit,
    Save,
    X,
    Eye,
    Info,
    UserCircle2,
    IdCard,
    Download,
    Share2,
} from "lucide-react";

import {
    useEffect,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    updateProfileAction,
} from "../../../state/actions/authActions";

import {
    updateDriverPartnerAction,
    fetchMyDriverPartnerAction,
} from "../../../state/actions/driverPartnerActions";

import {
    toaster,
} from "../../../components/toaster";

import ActivityToast from "../../../components/ActivityToast";
import Card from "../../Dashboard/components/Card";
import DriverPartnerPoster from "./DriverPartnerPoster";
import AttachmentUpload from "../../../components/AttachmentUpload";

const paymentMethods = createListCollection({
    items: [
        {
            label: "Bank Transfer",
            value: "Bank",
        },
        {
            label: "Orange Money",
            value: "OrangeMoney",
        },
        {
            label: "MyZaka",
            value: "MyZaka",
        },
        {
            label: "Smega",
            value: "Smega",
        },
        {
            label: "Other",
            value: "Other",
        },
    ],
});

const PersonalInformation = ({ driverPartner }) => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchMyDriverPartnerAction());
    }, [dispatch]);

    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const [editingDocuments, setEditingDocuments] = useState(false);

    const [documentForm, setDocumentForm] = useState({
        identity_proof: null,
        profile_image: null,
    });

    // Files selected through AttachmentUpload
    const [identityProofFiles, setIdentityProofFiles] = useState([]);
    const [profileImageFiles, setProfileImageFiles] = useState([]);

    const { updatingProfile } = useSelector(
        (state) => state.auth
    );

    const { updating } = useSelector(
        (state) => state.driverPartner
    );

    const user = driverPartner?.user;

    const [editingPersonal, setEditingPersonal] = useState(false);
    const [editingPartner, setEditingPartner] = useState(false);

    const [personalForm, setPersonalForm] = useState({});
    const [partnerForm, setPartnerForm] = useState({});

    useEffect(() => {
        if (!user) return;

        setPersonalForm({
            first_name: user.first_name || "",
            middle_name: user.middle_name || "",
            last_name: user.last_name || "",
            phone_number: user.phone_number || "",
            district: user.district || "",
            village: user.village || "",
            physical_address: user.physical_address || "",
            postal_address: user.postal_address || "",
            id_or_passport_number: user.id_or_passport_number || "",
        });

        setPartnerForm({
            payment_method: driverPartner?.payment_method || "",
            payment_account: driverPartner?.payment_account || "",
            account_name: driverPartner?.account_name || "",
            account_number: driverPartner?.account_number || "",
            branch: driverPartner?.branch || "",
            branch_code: driverPartner?.branch_code || "",
            identity_proof: null,
            profile_image: null,
        });
    }, [user, driverPartner]);

    const API_URL = process.env.REACT_APP_API_BASE_URL?.replace(
        /\/+$/,
        ""
    );

    const getDocumentUrl = (filePath) =>
        filePath
            ? `${API_URL}/${filePath.replace(/^\/+/, "")}`
            : null;

    const handleSavePersonal = async () => {
        const result = await dispatch(
            updateProfileAction(personalForm)
        );

        if (result?.success) {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="success"
                        title="Profile Updated"
                        description="Your personal details were updated successfully."
                        timestamp="Just now"
                    />
                ),
            });

            setEditingPersonal(false);
        } else {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="error"
                        title="Update Failed"
                        description={
                            result?.error ||
                            "Unable to update profile."
                        }
                        timestamp="Just now"
                    />
                ),
            });
        }
    };

    const handleSaveDocuments = async () => {
        const payload = {
            identity_proof: documentForm.identity_proof,
            profile_image: documentForm.profile_image,
        };

        const result = await dispatch(
            updateDriverPartnerAction(
                driverPartner.driver_partner_id,
                payload
            )
        );

        if (result?.success) {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="success"
                        title="Documents Updated"
                        description="Your driver documents were updated successfully."
                        timestamp="Just now"
                    />
                ),
            });

            await dispatch(fetchMyDriverPartnerAction());

            setDocumentForm({
                identity_proof: null,
                profile_image: null,
            });

            setIdentityProofFiles([]);
            setProfileImageFiles([]);

            setEditingDocuments(false);
        } else {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="error"
                        title="Update Failed"
                        description={
                            result?.error ||
                            "Unable to update documents."
                        }
                        timestamp="Just now"
                    />
                ),
            });
        }
    };

    const handleSavePartner = async () => {
        const payload = {
            payment_method: partnerForm.payment_method,
            payment_account: partnerForm.payment_account,
            account_name: partnerForm.account_name,
            account_number: partnerForm.account_number,
            branch: partnerForm.branch,
            branch_code: partnerForm.branch_code,
            identity_proof: partnerForm.identity_proof,
            profile_image: partnerForm.profile_image,
        };

        console.log("UPDATE DRIVER PARTNER PAYLOAD", payload);

        const result = await dispatch(
            updateDriverPartnerAction(
                driverPartner.driver_partner_id,
                payload
            )
        );

        if (result?.success) {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="success"
                        title="Profile Updated"
                        description="Your payment details and driver documents were updated successfully."
                        timestamp="Just now"
                    />
                ),
            });

            await dispatch(fetchMyDriverPartnerAction());
            setEditingPartner(false);
        } else {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="error"
                        title="Update Failed"
                        description={
                            result?.error ||
                            "Unable to update driver partner profile."
                        }
                        timestamp="Just now"
                    />
                ),
            });
        }
    };

    return (
        <Card>
            {/* DRIVER PARTNER POSTER */}
            <Box
                mt={6}
                p={5}
                border="1px solid"
                borderColor="#E7EAF3"
                borderRadius="18px"
                bg="#F8FAFF"
                mb={10}
            >
                <Flex
                    direction={{
                        base: "column",
                        md: "row",
                    }}
                    align={{
                        base: "flex-start",
                        md: "center",
                    }}
                    justify="space-between"
                    gap={4}
                >
                    <Flex align="center" gap={4}>
                        <Flex
                            w="42px"
                            h="42px"
                            borderRadius="12px"
                            bg="#EAF0FF"
                            color="#2453FF"
                            align="center"
                            justify="center"
                            flexShrink={0}
                        >
                            <Share2 size={20} />
                        </Flex>

                        <Box>
                            <Text
                                fontSize="15px"
                                fontWeight="700"
                                color="#071D54"
                            >
                                Promote your Mogoma profile
                            </Text>

                            <Text
                                fontSize="13px"
                                color="gray.500"
                                mt={1}
                            >
                                Create a professional poster to
                                share with your network.
                            </Text>
                        </Box>
                    </Flex>

                    <DriverPartnerPoster
                        user={user}
                        driverPartner={driverPartner}
                        mediaBaseUrl={API_URL}
                    />
                </Flex>
            </Box>

            {/* PERSONAL DETAILS */}
            <Flex
                direction={{
                    base: "column",
                    md: "row",
                }}
                align={{
                    base: "stretch",
                    md: "center",
                }}
                justify="space-between"
                gap={4}
                mb={5}
            >
                <Text
                    fontSize="lg"
                    fontWeight="700"
                    color="#071D54"
                >
                    Personal Details
                </Text>

                <HStack
                    gap={2}
                    w={{
                        base: "100%",
                        md: "auto",
                    }}
                >
                    {editingPersonal && (
                        <Button
                            size="sm"
                            colorPalette="blue"
                            loading={updatingProfile}
                            loadingText="Saving..."
                            onClick={handleSavePersonal}
                            flex={{
                                base: 1,
                                md: "unset",
                            }}
                        >
                            <Save size={16} />
                            Save
                        </Button>
                    )}

                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                            setEditingPersonal(!editingPersonal)
                        }
                        flex={{
                            base: 1,
                            md: "unset",
                        }}
                    >
                        {editingPersonal ? (
                            <>
                                <X size={16} />
                                Cancel
                            </>
                        ) : (
                            <>
                                <Edit size={16} />
                                Edit Personal Details
                            </>
                        )}
                    </Button>
                </HStack>
            </Flex>

            <Grid
                templateColumns={{
                    base: "1fr",
                    md: "1fr 1fr",
                }}
                gap={5}
                mb={10}
            >
                <InputField
                    label="First Name"
                    value={personalForm.first_name}
                    editable={editingPersonal}
                    onChange={(value) =>
                        setPersonalForm((prev) => ({
                            ...prev,
                            first_name: value,
                        }))
                    }
                />

                <InputField
                    label="Middle Name"
                    value={personalForm.middle_name}
                    editable={editingPersonal}
                    onChange={(value) =>
                        setPersonalForm((prev) => ({
                            ...prev,
                            middle_name: value,
                        }))
                    }
                />

                <InputField
                    label="Last Name"
                    value={personalForm.last_name}
                    editable={editingPersonal}
                    onChange={(value) =>
                        setPersonalForm((prev) => ({
                            ...prev,
                            last_name: value,
                        }))
                    }
                />

                <InputField
                    label="Username"
                    value={user?.username}
                />

                <InputField
                    label="Email Address"
                    value={user?.email}
                />

                <InputField
                    label="Phone Number"
                    value={personalForm.phone_number}
                    editable={editingPersonal}
                    onChange={(value) =>
                        setPersonalForm((prev) => ({
                            ...prev,
                            phone_number: value,
                        }))
                    }
                />

                <InputField
                    label="District"
                    value={personalForm.district}
                    editable={editingPersonal}
                    onChange={(value) =>
                        setPersonalForm((prev) => ({
                            ...prev,
                            district: value,
                        }))
                    }
                />

                <InputField
                    label="Village"
                    value={personalForm.village}
                    editable={editingPersonal}
                    onChange={(value) =>
                        setPersonalForm((prev) => ({
                            ...prev,
                            village: value,
                        }))
                    }
                />

                <InputField
                    label="Physical Address"
                    value={personalForm.physical_address}
                    editable={editingPersonal}
                    onChange={(value) =>
                        setPersonalForm((prev) => ({
                            ...prev,
                            physical_address: value,
                        }))
                    }
                />

                <InputField
                    label="Postal Address"
                    value={personalForm.postal_address}
                    editable={editingPersonal}
                    onChange={(value) =>
                        setPersonalForm((prev) => ({
                            ...prev,
                            postal_address: value,
                        }))
                    }
                />

                <InputField
                    label="ID / Passport Number"
                    value={personalForm.id_or_passport_number}
                    editable={editingPersonal}
                    onChange={(value) =>
                        setPersonalForm((prev) => ({
                            ...prev,
                            id_or_passport_number: value,
                        }))
                    }
                />
            </Grid>

            {/* PAYMENT DETAILS */}
            <Flex
                direction={{
                    base: "column",
                    md: "row",
                }}
                align={{
                    base: "stretch",
                    md: "center",
                }}
                justify="space-between"
                gap={4}
                mb={5}
            >
                <Text
                    fontSize="lg"
                    fontWeight="700"
                    color="#071D54"
                >
                    Payment Details
                </Text>

                <HStack
                    gap={2}
                    w={{
                        base: "100%",
                        md: "auto",
                    }}
                >
                    {editingPartner && (
                        <Button
                            size="sm"
                            colorPalette="blue"
                            loading={updating}
                            loadingText="Saving..."
                            onClick={handleSavePartner}
                            flex={{
                                base: 1,
                                md: "unset",
                            }}
                        >
                            <Save size={16} />
                            Save
                        </Button>
                    )}

                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                            setEditingPartner(!editingPartner)
                        }
                        flex={{
                            base: 1,
                            md: "unset",
                        }}
                    >
                        {editingPartner ? (
                            <>
                                <X size={16} />
                                Cancel
                            </>
                        ) : (
                            <>
                                <Edit size={16} />
                                Edit Payment Details
                            </>
                        )}
                    </Button>
                </HStack>
            </Flex>

            <Grid
                templateColumns={{
                    base: "1fr",
                    md: "1fr 1fr",
                }}
                gap={5}
                mb={10}
            >
                <Box>
                    <Text
                        mb={2}
                        color="gray.500"
                        fontWeight="600"
                        fontSize="sm"
                    >
                        Payment Method
                    </Text>

                    {editingPartner ? (
                        <Select.Root
                            collection={paymentMethods}
                            value={[partnerForm.payment_method]}
                            onValueChange={(e) =>
                                setPartnerForm((prev) => ({
                                    ...prev,
                                    payment_method: e.value[0],
                                }))
                            }
                        >
                            <Select.Trigger>
                                <Select.ValueText placeholder="Select payment method" />
                                <Select.Indicator />
                            </Select.Trigger>

                            <Select.Content>
                                {paymentMethods.items.map((item) => (
                                    <Select.Item
                                        key={item.value}
                                        item={item}
                                    >
                                        {item.label}
                                    </Select.Item>
                                ))}
                            </Select.Content>
                        </Select.Root>
                    ) : (
                        <Input
                            value={partnerForm.payment_method}
                            readOnly
                            bg="gray.50"
                        />
                    )}
                </Box>

                <InputField
                    label={
                        partnerForm.payment_method === "Bank"
                            ? "Bank Name"
                            : "Payment Number"
                    }
                    value={partnerForm.payment_account}
                    editable={editingPartner}
                    onChange={(value) =>
                        setPartnerForm((prev) => ({
                            ...prev,
                            payment_account: value,
                        }))
                    }
                />

                {partnerForm.payment_method === "Bank" && (
                    <>
                        <InputField
                            label="Account Name"
                            value={partnerForm.account_name}
                            editable={editingPartner}
                            onChange={(value) =>
                                setPartnerForm((prev) => ({
                                    ...prev,
                                    account_name: value,
                                }))
                            }
                        />

                        <InputField
                            label="Account Number"
                            value={partnerForm.account_number}
                            editable={editingPartner}
                            onChange={(value) =>
                                setPartnerForm((prev) => ({
                                    ...prev,
                                    account_number: value,
                                }))
                            }
                        />

                        <InputField
                            label="Branch"
                            value={partnerForm.branch}
                            editable={editingPartner}
                            onChange={(value) =>
                                setPartnerForm((prev) => ({
                                    ...prev,
                                    branch: value,
                                }))
                            }
                        />

                        <InputField
                            label="Branch Code"
                            value={partnerForm.branch_code}
                            editable={editingPartner}
                            onChange={(value) =>
                                setPartnerForm((prev) => ({
                                    ...prev,
                                    branch_code: value,
                                }))
                            }
                        />
                    </>
                )}
            </Grid>

            {/* DOCUMENTS */}
            <Box mt={10}>
                {/* DOCUMENT HEADER */}
                <Flex
                    direction={{
                        base: "column",
                        md: "row",
                    }}
                    align={{
                        base: "stretch",
                        md: "center",
                    }}
                    justify="space-between"
                    gap={4}
                    mb={6}
                >
                    <Text
                        fontSize="lg"
                        fontWeight="700"
                        color="#071D54"
                    >
                        Uploaded Documents
                    </Text>

                    <HStack
                        gap={2}
                        w={{
                            base: "100%",
                            md: "auto",
                        }}
                    >
                        {editingDocuments && (
                            <Button
                                size="sm"
                                colorPalette="blue"
                                loading={updating}
                                loadingText="Saving..."
                                onClick={handleSaveDocuments}
                                flex={{
                                    base: 1,
                                    md: "unset",
                                }}
                            >
                                <Save size={16} />
                                Save
                            </Button>
                        )}

                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                if (editingDocuments) {
                                    setDocumentForm({
                                        identity_proof: null,
                                        profile_image: null,
                                    });

                                    setIdentityProofFiles([]);
                                    setProfileImageFiles([]);
                                }

                                setEditingDocuments(!editingDocuments);
                            }}
                            flex={{
                                base: 1,
                                md: "unset",
                            }}
                        >
                            {editingDocuments ? (
                                <>
                                    <X size={16} />
                                    Cancel
                                </>
                            ) : (
                                <>
                                    <Edit size={16} />
                                    Edit Documents
                                </>
                            )}
                        </Button>
                    </HStack>
                </Flex>

                <VStack gap={5} align="stretch">
                    {[
                        {
                            type: "Identity Proof",
                            icon: <IdCard size={20} />,
                            description: "National ID Card",
                            accept: ".jpg,.jpeg,.png,.pdf",
                        },
                        {
                            type: "Profile Image",
                            icon: <UserCircle2 size={20} />,
                            description: "Headshot",
                            accept: ".jpg,.jpeg,.png,.webp",
                        },
                    ].map((documentType) => {
                        const document =
                            driverPartner?.documents?.find(
                                (doc) =>
                                    doc.document_type ===
                                    documentType.type
                            );

                        const isIdentity =
                            documentType.type === "Identity Proof";

                        const selectedFile = isIdentity
                            ? documentForm.identity_proof
                            : documentForm.profile_image;

                        const uploadFiles = isIdentity
                            ? identityProofFiles
                            : profileImageFiles;

                        const localPreview =
                            uploadFiles[0]?.preview || null;

                        return (
                            <Flex
                                key={documentType.type}
                                direction={
                                    editingDocuments
                                        ? "column"
                                        : {
                                              base: "column",
                                              md: "row",
                                          }
                                }
                                align={
                                    editingDocuments
                                        ? "stretch"
                                        : {
                                              base: "stretch",
                                              md: "center",
                                          }
                                }
                                justify="space-between"
                                gap={4}
                                p={5}
                                border="1px solid"
                                borderColor="#E7EAF3"
                                borderRadius="20px"
                                bg="white"
                            >
                                {/* DOCUMENT INFORMATION */}
                                <HStack gap={4} align="center">
                                    <Flex
                                        w="36px"
                                        h="36px"
                                        bg="#F4F7FF"
                                        borderRadius="16px"
                                        align="center"
                                        justify="center"
                                        color="#2453FF"
                                        flexShrink={0}
                                    >
                                        {documentType.icon}
                                    </Flex>

                                    <Box minW="0">
                                        <Text
                                            fontWeight="700"
                                            fontSize="17px"
                                            color="#071D54"
                                        >
                                            {documentType.type}
                                        </Text>

                                        {selectedFile ? (
                                            <Text
                                                color="#2453FF"
                                                fontSize="sm"
                                                fontWeight="600"
                                                maxW={{
                                                    base: "100%",
                                                    md: "300px",
                                                }}
                                                overflow="hidden"
                                                textOverflow="ellipsis"
                                                whiteSpace="nowrap"
                                                title={selectedFile.name}
                                            >
                                                {selectedFile.name}
                                            </Text>
                                        ) : document ? (
                                            <Text
                                                color="gray.500"
                                                fontSize="15px"
                                            >
                                                {documentType.description}{" "}
                                                •{" "}
                                                {document.file_extension}
                                            </Text>
                                        ) : (
                                            <Text
                                                color="red.500"
                                                fontSize="15px"
                                                fontWeight="600"
                                            >
                                                Document not uploaded
                                            </Text>
                                        )}
                                    </Box>
                                </HStack>

                                {/* DOCUMENT ACTIONS */}
                                <HStack
                                    gap={2}
                                    w={{
                                        base: "100%",
                                        md: "auto",
                                    }}
                                    justify={{
                                        base: "flex-start",
                                        md: "flex-end",
                                    }}
                                >
                                    {localPreview && (
                                        <Image
                                            src={localPreview}
                                            alt={selectedFile?.name || documentType.type}
                                            boxSize="45px"
                                            objectFit="cover"
                                            borderRadius="8px"
                                            border="1px solid"
                                            borderColor="gray.200"
                                        />
                                    )}

                                    {selectedFile?.type ===
                                        "application/pdf" && (
                                        <Box
                                            px={3}
                                            py={2}
                                            bg="#F4F7FF"
                                            borderRadius="8px"
                                        >
                                            <Text
                                                fontSize="xs"
                                                fontWeight="700"
                                                color="#2453FF"
                                            >
                                                PDF
                                            </Text>
                                        </Box>
                                    )}

                                    {document && !selectedFile && (
                                        <Button
                                            variant="ghost"
                                            color="#2453FF"
                                            onClick={() => {
                                                setSelectedDocument(document);
                                                setIsPreviewOpen(true);
                                            }}
                                        >
                                            <Eye
                                                size={15}
                                                style={{
                                                    marginRight: 8,
                                                }}
                                            />
                                            View
                                        </Button>
                                    )}
                                </HStack>

                                {/* REUSABLE ATTACHMENT UPLOAD */}
                                {editingDocuments && (
                                    <Box w="100%">
                                        <AttachmentUpload
                                            title={
                                                isIdentity
                                                    ? "Upload Identity Proof"
                                                    : "Upload Profile Photo"
                                            }
                                            description={
                                                isIdentity
                                                    ? "Upload a clear image or PDF of your National ID."
                                                    : "Upload a clear passport-style photo showing your face."
                                            }
                                            files={
                                                isIdentity
                                                    ? identityProofFiles
                                                    : profileImageFiles
                                            }
                                            onChange={(items) => {
                                                const safeItems =
                                                    Array.isArray(items)
                                                        ? items.slice(0, 1)
                                                        : [];

                                                const file =
                                                    safeItems[0]?.file ?? null;

                                                if (isIdentity) {
                                                    setIdentityProofFiles(
                                                        safeItems
                                                    );

                                                    setDocumentForm((prev) => ({
                                                        ...prev,
                                                        identity_proof: file,
                                                    }));
                                                } else {
                                                    setProfileImageFiles(
                                                        safeItems
                                                    );

                                                    setDocumentForm((prev) => ({
                                                        ...prev,
                                                        profile_image: file,
                                                    }));
                                                }
                                            }}
                                            maxFiles={1}
                                            accept={documentType.accept}
                                        />
                                    </Box>
                                )}
                            </Flex>
                        );
                    })}
                </VStack>
            </Box>

            {/* DOCUMENT PREVIEW DIALOG */}
            <Dialog.Root
                open={isPreviewOpen}
                onOpenChange={(e) => setIsPreviewOpen(e.open)}
                size="md"
            >
                <Dialog.Backdrop />

                <Dialog.Positioner>
                    <Dialog.Content
                        maxW={{
                            base: "calc(100vw - 32px)",
                            md: "700px",
                        }}
                        mx={{
                            base: 4,
                            md: 0,
                        }}
                        borderRadius="xl"
                    >
                        <Dialog.Header
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Dialog.Title>
                                {selectedDocument?.document_type}
                            </Dialog.Title>

                            <Dialog.CloseTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    minW="unset"
                                    p={2}
                                >
                                    <X size={18} />
                                </Button>
                            </Dialog.CloseTrigger>
                        </Dialog.Header>

                        <Dialog.Body pb={6}>
                            {selectedDocument &&
                                (() => {
                                    const url = getDocumentUrl(
                                        selectedDocument.file_path
                                    );

                                    const ext =
                                        selectedDocument.file_extension?.toLowerCase();

                                    if (
                                        [
                                            "jpg",
                                            "jpeg",
                                            "png",
                                            "gif",
                                            "webp",
                                        ].includes(ext)
                                    ) {
                                        return (
                                            <Image
                                                src={url}
                                                alt={
                                                    selectedDocument.document_type
                                                }
                                                maxH="500px"
                                                w="100%"
                                                objectFit="contain"
                                                borderRadius="md"
                                            />
                                        );
                                    }

                                    if (ext === "pdf") {
                                        return (
                                            <iframe
                                                title="Document Preview"
                                                src={url}
                                                width="100%"
                                                height="500px"
                                                style={{
                                                    border: "none",
                                                    borderRadius: "8px",
                                                }}
                                            />
                                        );
                                    }

                                    return (
                                        <Box
                                            textAlign="center"
                                            py={8}
                                        >
                                            <Text mb={4}>
                                                Preview is not available for
                                                this document.
                                            </Text>

                                            <Button
                                                as="a"
                                                href={url}
                                                target="_blank"
                                                colorPalette="blue"
                                            >
                                                Open Document
                                            </Button>
                                        </Box>
                                    );
                                })()}
                        </Dialog.Body>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Dialog.Root>
        </Card>
    );
};

const InputField = ({
    label,
    value,
    editable = false,
    onChange,
}) => (
    <Box>
        <Text
            mb={2}
            color="gray.500"
            fontWeight="600"
            fontSize="sm"
        >
            {label}
        </Text>

        <Input
            value={value || ""}
            readOnly={!editable}
            onChange={(e) => onChange?.(e.target.value)}
            bg={editable ? "white" : "gray.50"}
        />
    </Box>
);

export default PersonalInformation;